import React, { useEffect, useState } from 'react';
import { Route, Switch } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import {
  sendMobileErrorNotification,
  upsellTransition,
  UpsellTransitionStages,
} from '@ecs-uk/web-common-mobile-view-helpers';
import MobileSwitch from './switch';
import getCustomer from '../../actions/auth/getCustomer';
import getAllOffers from '../../actions/offers/getAll';
import getAllSubscriptions from '../../actions/subscriptions/getAll';
import getScore from '../../actions/scores/getScore';
import getBillingProvider from '../../actions/billing/getBillingProvider';
import BILLING_PROVIDERS from '../../constants/billingProviders';
import { getPaymentMethods } from '../../actions/billing/paymentMethods';
import { getBilling } from '../../actions/billing';
import MobilePaymentAndBilling from './payment-and-billing';
import MobileChangeBillingDatePage from './change-billing-date';
import MobileChangePaymentMethod from './payment-and-billing/change-payment-method';
import MobileDeactivatePage from './deactivate';
import MobileCancelSubscriptionPage from './cancel-subscription';
import MobileMySubscriptionPage from './my-subscription';
import MobileThanksLandingPage from './thanks';
import GuardedRoute from '../../components/GuardedRoute';
import useFlexData from '../../hooks/flexHooks/useFlexData';
import { useGetIsFlexUserQuery } from '../../services/entitlementsApi';
import setAnalyticsPlatformChannel from '../../actions/analyticsPlatform/setChannel';

const CORE_DEPENDENCIES = [getCustomer, getAllOffers, getAllSubscriptions, getPaymentMethods, getBilling, getScore];

// A home for Webview routes - will be responsible for anything that needs to be available to all routes
const MobileRoutes = () => {
  const [initialised, setInitialised] = useState(false);
  const [coreDependenciesLoaded, setCoreDependenciesLoaded] = useState(false);
  const mobileSwitchEnabled = useSelector(state => state.billing.billingProvider === BILLING_PROVIDERS.NEW);
  const hasCBDEarliestSelectableDate = useSelector(
    state => !!state.billing?.dateChangeEligibility?.data?.earliestSelectableDate
  );
  const splitsReady = useSelector(state => !!(state.split?.fetched || state.split?.error || state.auth?.error));
  const dispatch = useDispatch();

  const { error: isFlexUserError } = useGetIsFlexUserQuery(undefined);
  const { flexDataError, isFlexDataReady, isFlexUser } = useFlexData({ skip: !splitsReady });

  useEffect(() => {
    const initialise = async () => {
      /* Check we can establish a users billing provider before continuing, this will redirect to an error
    screen if the provider cannot be established */
      try {
        upsellTransition({ stage: UpsellTransitionStages.LOCK });
        await setAnalyticsPlatformChannel()(dispatch);
        await getBillingProvider()(dispatch);
        await Promise.allSettled(CORE_DEPENDENCIES.map(dependency => dependency()(dispatch)));
        setCoreDependenciesLoaded(true);
      } catch (error) {
        sendMobileErrorNotification();
      } finally {
        upsellTransition({ stage: UpsellTransitionStages.UNLOCK });
      }
    };

    initialise();
  }, [dispatch]);

  useEffect(() => {
    if (isFlexUserError || flexDataError) {
      sendMobileErrorNotification();
      return;
    }

    if (coreDependenciesLoaded && isFlexDataReady && splitsReady) {
      setInitialised(true);
    }
  }, [coreDependenciesLoaded, isFlexUserError, flexDataError, isFlexDataReady, splitsReady]);

  if (!initialised) {
    // a user is seeing a native spinner
    return null;
  }

  // core deps available, each route will be responsible for telling the app that it's loaded
  return (
    <Switch>
      <Route exact path="/mobile" render={() => <MobileMySubscriptionPage isBlockedByKillSwitch={!isFlexUser} />} />
      <Route
        exact
        path="/mobile/thanks"
        render={() => <MobileThanksLandingPage isBlockedByKillSwitch={!isFlexUser} />}
      />
      <Route exact path="/mobile/billing" component={MobilePaymentAndBilling} />
      <GuardedRoute
        exact
        path="/mobile/billing/change-date"
        component={MobileChangeBillingDatePage}
        canActivate={hasCBDEarliestSelectableDate}
        redirectTo="/mobile/billing"
      />
      <Route exact path="/mobile/deactivate" render={() => <MobileDeactivatePage isFlexUser={isFlexUser} />} />
      <Route
        exact
        path="/mobile/cancel"
        render={() => <MobileCancelSubscriptionPage isBlockedByKillSwitch={!isFlexUser} />}
      />
      <Route exact path="/mobile/switch" component={mobileSwitchEnabled ? MobileSwitch : null} />
      <Route exact path="/mobile/change-payment" component={MobileChangePaymentMethod} />
    </Switch>
  );
};

export default MobileRoutes;
